import { UNIFIED_THREAD_SIZES } from "@penn/shared-types/domain/unified-thread-sizes";

export const ANY_THREAD = "Any";

export type MaxThreadSizeOption = {
  value: string;
  label: string;
};

/**
 * Keep the selector resilient to malformed or unexpectedly unordered API data.
 * The service normally returns DISTINCT integers in ascending order, but the
 * browser still validates that boundary before rendering it.
 */
export function normalizeMaxThreadSizes(values: readonly unknown[]): number[] {
  return Array.from(
    new Set(
      values.filter(
        (value): value is number =>
          typeof value === "number" &&
          Number.isFinite(value) &&
          Number.isInteger(value) &&
          value > 0,
      ),
    ),
  ).sort((left, right) => left - right);
}

export type MaxThreadSizeGroup = {
  label: string;
  options: MaxThreadSizeOption[];
};

/**
 * NA (MICS-164): pass `reachesInRegion` to add the unified sizes whose metric
 * equivalent is offered and reached by a machine in the region; metric and
 * unified are then grouped. Their value is the unified label itself.
 */
export function buildMaxThreadSizeOptions(
  values: readonly unknown[],
  reachesInRegion?: (metricSize: number) => boolean,
): Array<MaxThreadSizeOption | MaxThreadSizeGroup> {
  const sizes = normalizeMaxThreadSizes(values);
  const any = { value: ANY_THREAD, label: ANY_THREAD };
  const metric = sizes.map((size) => ({
    value: String(size),
    label: `M${size}`,
  }));
  const unified = reachesInRegion
    ? UNIFIED_THREAD_SIZES.filter(
        (u) => sizes.includes(u.metricSize) && reachesInRegion(u.metricSize),
      ).map((u) => ({ value: u.label, label: u.label }))
    : [];
  if (unified.length === 0) return [any, ...metric];
  return [
    any,
    { label: "Metric", options: metric },
    { label: "Unified", options: unified },
  ];
}

/** Metric size an option filters by (`maxThreadSize`); null for Any. */
export function threadSizeToMetric(value: string): number | null {
  const unified = UNIFIED_THREAD_SIZES.find((u) => u.label === value);
  if (unified) return unified.metricSize;
  const size = Number(value);
  return Number.isInteger(size) && size > 0 ? size : null;
}

export function threadSizeLabel(value: string): string {
  if (value === ANY_THREAD) return ANY_THREAD;
  return UNIFIED_THREAD_SIZES.some((u) => u.label === value)
    ? value
    : `M${value}`;
}
